import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    Share2, Download, Copy, CheckCircle2, Instagram, Linkedin,
    Loader2, Star, Sparkles, ExternalLink, QrCode, Eye,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Homly — Studio de partage (viral loop "Strava-like")
 *
 * L'agent connecté prévisualise ses 4 assets prêts à publier
 * (Story / Post / Reel / Cover LinkedIn) et peut :
 *   • Télécharger en PNG (conversion client via <canvas>)
 *   • Copier le lien direct du visuel
 *   • Copier le lien de son profil public
 *
 * Chaque asset embarque déjà un QR + l'URL courte qui pointent
 * vers `homly.pro/agent/{slug}` — c'est ce qui alimente la
 * vitalité du réseau (comme le partage Strava).
 */
export default function ShareStudioPage() {
    const [data, setData] = useState(null);
    const [views, setViews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState('story');
    const [downloading, setDownloading] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [formatsRes, viewsRes] = await Promise.all([
                api.get('/agent/me/share/formats'),
                api.get('/agent/me/badge/views').catch(() => null),
            ]);
            setData(formatsRes.data);
            if (viewsRes) setViews(viewsRes.data);
        } catch (e) {
            toast.error("Impossible de charger le studio. Réessayez dans un instant.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Le backend renvoie une URL absolue (partage externe) et une URL
    // relative (aperçu interne). En preview/dev, l'URL absolue pointe
    // parfois vers un domaine non-accessible : on utilise la relative
    // pour l'aperçu et l'absolue pour la copie/partage.
    const backendBase = process.env.REACT_APP_BACKEND_URL || '';
    const previewUrl = (f) => `${backendBase}${f.relative_url || ''}`;

    const selected = useMemo(
        () => data?.formats?.find(f => f.key === selectedKey) || null,
        [data, selectedKey]
    );

    // ── Utilitaires ────────────────────────────────────────────
    const copyToClipboard = async (text, message = 'Copié !') => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(message);
        } catch {
            toast.error('Impossible de copier');
        }
    };

    /**
     * Partage natif via Web Share API (iOS/Android).
     * Sur mobile : ouvre la feuille de partage native (Insta/WhatsApp/LinkedIn/etc).
     * Sur desktop : fallback copie du lien.
     */
    const nativeShare = async (fmt) => {
        const shareUrl = fmt.url || `${backendBase}${fmt.relative_url}`;
        const title = `Mes avis vérifiés Homly — ${data?.agent_name || ''}`;
        const text = `Voici mon badge de confiance et mes avis vérifiés sur Homly.`;
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url: shareUrl });
                toast.success('Partagé !');
            } catch (e) {
                if (e?.name !== 'AbortError') toast.error('Partage annulé');
            }
        } else {
            // Desktop fallback : copie du lien
            await copyToClipboard(shareUrl, 'Lien copié — collez-le sur vos réseaux');
        }
    };

    /**
     * Conversion SVG → PNG côté client via <canvas>.
     * On charge le SVG, on le rasterise à sa taille native
     * (1080×1920, 1080×1080, 1080×1350, 1200×628) puis on
     * déclenche un téléchargement.
     */
    const downloadAsPng = async (fmt) => {
        setDownloading(fmt.key);
        try {
            // Charger le SVG en texte (via l'URL interne accessible en dev/preview)
            const svgRes = await fetch(previewUrl(fmt), { cache: 'no-store' });
            const svgText = await svgRes.text();

            // Extraire les dimensions natives (viewBox)
            const [w, h] = fmt.dimensions.split('×').map(n => parseInt(n, 10));

            // Encoder le SVG en data-URL
            const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            // Charger dans une <img>, dessiner sur <canvas>, exporter en PNG
            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
            });

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            // Fond noir Homly pour les cas où le SVG a de la transparence
            ctx.fillStyle = '#050308';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);

            const pngUrl = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = pngUrl;
            a.download = `homly-${data?.slug || 'agent'}-${fmt.key}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`${fmt.label} téléchargé !`);
        } catch (e) {
            toast.error('Téléchargement échoué. Réessayez.');
        } finally {
            setDownloading(null);
        }
    };

    // ── Rendu ──────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#050308]" data-testid="studio-loading">
                <Loader2 className="w-8 h-8 text-[#A855F7] animate-spin" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#050308] text-white p-8" data-testid="studio-error">
                <div className="text-center max-w-md">
                    <p className="text-lg font-semibold mb-2">Studio indisponible</p>
                    <p className="text-sm text-[#8A7DB8]">Réessayez dans un instant.</p>
                </div>
            </div>
        );
    }

    const { stats, agent_name, profile_url, badge_url } = data;
    const hasReviews = stats?.count > 0;

    return (
        <div className="min-h-screen bg-[#050308] text-white pb-24" data-testid="share-studio-page">
            {/* Halos ambiants (DA Homly landing) */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[#681793]/20 blur-[120px] rounded-full" />
                <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] bg-[#FF914D]/15 blur-[140px] rounded-full" />
            </div>

            <div className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-10 sm:pt-14">
                {/* ── Header ────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-10"
                >
                    <div className="flex items-center gap-2 text-xs font-semibold tracking-[3px] text-[#8A7DB8] mb-3">
                        <Sparkles className="w-4 h-4 text-[#FF914D]" />
                        STUDIO DE PARTAGE
                    </div>
                    <h1
                        className="text-4xl sm:text-5xl font-extrabold leading-tight bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent mb-3"
                        data-testid="studio-title"
                    >
                        Partagez votre confiance
                    </h1>
                    <p className="text-base text-[#C7BEE6] max-w-2xl">
                        Vos avis vérifiés en visuels prêts à publier sur Instagram, LinkedIn ou Facebook.
                        Chaque visuel embarque un QR code qui ramène vers votre profil Homly.
                    </p>
                </motion.div>

                {/* ── KPI strip ─────────────────────────────────── */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10">
                    <KpiCard
                        label="Avis vérifiés"
                        value={stats?.count ?? 0}
                        icon={<CheckCircle2 className="w-5 h-5" />}
                        testid="kpi-count"
                    />
                    <KpiCard
                        label="Note moyenne"
                        value={hasReviews ? `${stats.avg_rating.toFixed(1)}/5` : '—'}
                        icon={<Star className="w-5 h-5" />}
                        testid="kpi-avg"
                    />
                    <KpiCard
                        label="Recommandent"
                        value={hasReviews ? `${stats.recommend_rate}%` : '—'}
                        icon={<Sparkles className="w-5 h-5" />}
                        testid="kpi-rec"
                    />
                    <KpiCard
                        label="Vues cette semaine"
                        value={views?.last_7d ?? 0}
                        hint={views ? `${views.total} vues au total` : null}
                        icon={<Eye className="w-5 h-5" />}
                        testid="kpi-views"
                    />
                </div>

                {/* ── Format picker ─────────────────────────────── */}
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-6" data-testid="format-picker">
                    {data.formats.map(f => (
                        <button
                            key={f.key}
                            onClick={() => setSelectedKey(f.key)}
                            data-testid={`format-tab-${f.key}`}
                            className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                                selectedKey === f.key
                                    ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] border-transparent text-white shadow-lg shadow-[#681793]/30'
                                    : 'bg-white/5 border-white/10 text-[#C7BEE6] hover:bg-white/10 hover:border-[#A855F7]/40'
                            }`}
                        >
                            <FormatIcon k={f.key} />
                            <span className="ml-2">{f.label}</span>
                            <span className="ml-2 text-[10px] font-normal opacity-70">{f.dimensions}</span>
                        </button>
                    ))}
                </div>

                {/* ── Preview + actions ────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-start">
                    {/* Live preview */}
                    <motion.div
                        key={selectedKey}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.25 }}
                        className="rounded-3xl border border-[#A855F7]/20 bg-black/40 backdrop-blur-md p-4 sm:p-6"
                        data-testid="format-preview-container"
                    >
                        <div className={`mx-auto ${previewSizeClass(selectedKey)}`}>
                            <div className="relative w-full" style={{ aspectRatio: aspectRatioFor(selectedKey) }}>
                                <img
                                    src={selected ? previewUrl(selected) : ''}
                                    alt={selected?.label}
                                    className="w-full h-full object-contain rounded-2xl"
                                    data-testid={`preview-img-${selectedKey}`}
                                />
                            </div>
                        </div>
                    </motion.div>

                    {/* Actions column */}
                    <div className="w-full lg:w-72 space-y-3">
                        <ActionButton
                            onClick={() => downloadAsPng(selected)}
                            loading={downloading === selectedKey}
                            testid="btn-download-current"
                            primary
                        >
                            <Download className="w-4 h-4" />
                            Télécharger PNG
                        </ActionButton>

                        <ActionButton
                            onClick={() => nativeShare(selected)}
                            testid="btn-native-share"
                        >
                            <Share2 className="w-4 h-4" />
                            Partager sur mes réseaux
                        </ActionButton>

                        <ActionButton
                            onClick={() => copyToClipboard(selected?.url, 'Lien du visuel copié')}
                            testid="btn-copy-svg-url"
                        >
                            <Copy className="w-4 h-4" />
                            Copier le lien du visuel
                        </ActionButton>

                        <ActionButton
                            onClick={() => copyToClipboard(profile_url, 'Lien de votre profil copié')}
                            testid="btn-copy-profile-url"
                        >
                            <Share2 className="w-4 h-4" />
                            Copier lien de mon profil
                        </ActionButton>

                        <a
                            href={profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-open-profile"
                            className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-[#C7BEE6] transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Voir mon profil public
                        </a>

                        {/* Info bloc QR */}
                        <div className="mt-4 rounded-2xl border border-[#A855F7]/15 bg-black/30 p-4">
                            <div className="flex items-center gap-2 text-xs font-semibold text-[#A855F7] mb-2">
                                <QrCode className="w-4 h-4" />
                                QR intégré
                            </div>
                            <p className="text-xs text-[#8A7DB8] leading-relaxed">
                                Chaque visuel contient un QR scannable qui amène vos abonnés
                                directement sur votre profil Homly (comme sur Strava).
                            </p>
                        </div>
                    </div>
                </div>

                {/* ── Download all + badge ─────────────────────── */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
                        <h3 className="text-lg font-bold mb-1">Télécharger tout d&apos;un coup</h3>
                        <p className="text-sm text-[#8A7DB8] mb-4">Récupérez les 4 formats en PNG haute résolution.</p>
                        <div className="grid grid-cols-2 gap-2">
                            {data.formats.map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => downloadAsPng(f)}
                                    disabled={downloading === f.key}
                                    data-testid={`btn-download-${f.key}`}
                                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 hover:border-[#A855F7]/40 text-xs font-semibold text-white transition-colors disabled:opacity-50"
                                >
                                    {downloading === f.key
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Download className="w-3.5 h-3.5" />}
                                    {f.label.split(' ')[0]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
                        <h3 className="text-lg font-bold mb-1">Badge pour votre site</h3>
                        <p className="text-sm text-[#8A7DB8] mb-4">Le sceau à intégrer sur votre site web ou signature email.</p>
                        <div className="flex items-center gap-4">
                            <img
                                src={`${backendBase}${data.badge_relative_url || ''}`}
                                alt="Badge Homly"
                                className="w-24 h-auto rounded-xl"
                                data-testid="my-badge-preview"
                            />
                            <button
                                onClick={() => copyToClipboard(badge_url, 'Lien du badge copié')}
                                data-testid="btn-copy-badge-url"
                                className="flex-1 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#681793] to-[#A855F7] text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                            >
                                Copier le lien du badge
                            </button>
                        </div>
                    </div>
                </div>

                {/* Signature bas de page */}
                <p className="mt-14 text-center text-xs tracking-[3px] text-[#6B5A99]" data-testid="studio-footer">
                    {agent_name?.toUpperCase()}  ·  HOMLY.PRO
                </p>
            </div>
        </div>
    );
}

// ─── Sous-composants ──────────────────────────────────────────
const KpiCard = ({ label, value, hint, icon, testid }) => (
    <div
        className="rounded-2xl border border-[#A855F7]/15 bg-black/40 backdrop-blur-sm p-4"
        data-testid={testid}
    >
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[2px] text-[#8A7DB8] mb-2">
            <span className="text-[#FF914D]">{icon}</span>
            {label.toUpperCase()}
        </div>
        <div className="text-2xl font-extrabold bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
            {value}
        </div>
        {hint && <div className="text-[10px] text-[#6B5A99] mt-1">{hint}</div>}
    </div>
);

const ActionButton = ({ children, onClick, loading, primary, testid }) => (
    <button
        onClick={onClick}
        disabled={loading}
        data-testid={testid}
        className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-sm font-semibold transition-all disabled:opacity-60 ${
            primary
                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#681793]/30 hover:opacity-95'
                : 'bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-[#A855F7]/40'
        }`}
    >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
    </button>
);

const FormatIcon = ({ k }) => {
    if (k === 'cover') return <Linkedin className="w-3.5 h-3.5 inline" />;
    return <Instagram className="w-3.5 h-3.5 inline" />;
};

const aspectRatioFor = (k) => {
    switch (k) {
        case 'story': return '9 / 16';
        case 'post':  return '1 / 1';
        case 'card':  return '4 / 5';
        case 'cover': return '1200 / 628';
        default: return '1 / 1';
    }
};

const previewSizeClass = (k) => {
    switch (k) {
        case 'story': return 'max-w-[320px] sm:max-w-[380px]';
        case 'card':  return 'max-w-[380px] sm:max-w-[440px]';
        case 'post':  return 'max-w-[420px] sm:max-w-[520px]';
        case 'cover': return 'max-w-full';
        default: return 'max-w-md';
    }
};
